import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * DropdownInputMC component
 * Renders a dropdown input that allows the user to select from a list of options.
 *
 * @param {Function} onSelect - **Optional** - Function to be called when an option is selected or the input value changes. It receives the selected value (`String`) as an argument.
 * @param {Array<IOptionProps>} options - **Required** - Array of option objects to populate the dropdown.
 * @param {boolean} isTextArea - **Optional** - If `true`, renders a textarea instead of an input.
 * @param {number} rows - **Optional** - Number of rows to display in the textarea.
 * @param {string} defaultValue - **Optional** - Default value to display in the input/textarea.
 *
 * @typedef {Object} IOptionProps
 * @property {string} name - **Required** - Text to display in the dropdown list for this option.
 * @property {string} value - **Required** - Value to insert in the input/textarea when the option is selected.
 * @property {bool} fullWidth - **Optional** - If `true`, the option will take the full width of the dropdown container on screen.
 * @property {string} className - **Optional** - Custom class to apply to the dropdown container.
 */
const DropdownInputMC = ({
  onSelect, options = [], isTextArea = false, rows, fullWidth, defaultValue, className
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [defaultValueIsSet, setDefaultValueIsSet] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleOptionClick = (optionLabel) => {
    const { selectionStart, selectionEnd } = inputRef.current;
    const newValue = isTextArea
      ? inputValue.substring(0, inputValue.lastIndexOf('@'))
        + optionLabel
        + inputValue.substring(selectionEnd)
      : inputValue.substring(0, selectionStart) + optionLabel + inputValue.substring(selectionEnd);

    setInputValue(newValue);
    if (onSelect) {
      onSelect(newValue);
    }

    if (inputRef.current) {
      const newPosition = newValue.length;
      inputRef.current.setSelectionRange(newPosition, newPosition);
    }

    setIsOpen(false);
    if (isTextArea && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (event) => {
    // necesito obtener el valor de la último tecla presionada, que no es necesariamente el última, ya que el cursor puede estar en cualquier parte del string
    const cursorPosition = event.target.selectionStart;
    const lastChar = event.target.value[cursorPosition - 1];
    const newValue = event.target.value;
    setInputValue(newValue);
    if (onSelect) {
      onSelect(newValue);
    }
    if (isTextArea) {
      if (lastChar === '@') {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  };

  const handleInputClick = () => {
    if (!isTextArea) {
      setIsOpen(!isOpen);
    } else if (inputValue.includes('@')) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen
        && dropdownRef.current
        && !dropdownRef.current.contains(event.target)
        && inputRef.current !== event.target
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!inputValue && defaultValue && !defaultValueIsSet) {
      setDefaultValueIsSet(true);
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  const buttonClassName = [
    'tw-cursor-pointer',
    'tw-border-b',
    'tw-border-blue-400',
    'tw-text-gray-900',
    'tw-text-black',
    'tw-min-w-48',
    'tw-items-center',
    'focus:tw-outline-none',
    'tw-text-center'
  ].join(' ');

  const textareaClassName = [
    'tw-block',
    'tw-rounded-lg',
    'tw-px-2.5',
    'tw-py-2.5',
    'tw-w-full',
    'tw-text-sm',
    'tw-bg-gray-50',
    'tw-border-0',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-0',
    'peer',
    'tw-placeholder-blue-500'
  ].join(' ');

  const dropdownClass = [
    'tw-absolute',
    'tw-bg-white',
    'tw-border',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-z-10',
    'tw-mt-1',
    'tw-rigth',
    fullWidth ? 'tw-left-4 tw-right-4' : ''
  ].join(' ');

  const listItemClassName = [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ].join(' ');

  const inputProps = {
    ref: inputRef,
    value: inputValue,
    onChange: handleInputChange,
    onClick: handleInputClick,
    className: isTextArea ? textareaClassName : buttonClassName
  };

  return (
    <div className={`tw-wrapper tw-inline-block ${className || ''}`} ref={dropdownRef}>
      <div className="tw-relative">
        {isTextArea ? (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <textarea {...inputProps} rows={rows} />
        ) : (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <input type="text" {...inputProps} />
        )}
      </div>

      {isOpen && (
        <div className={dropdownClass}>
          <div className="tw-border-b tw-border-gray-200">
            <span className="tw-font-bold tw-px-3 tw-py-2 tw-text-sm tw-flex">
              {t('dropdown_input_mc.label')}
            </span>
          </div>
          <ul className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-text-sm tw-pl-0">
            {options.length === 0 ? (
              <li className="tw-px-4 tw-py-2 tw-text-gray-500">
                {t('dropdown_input_mc.no_options')}
              </li>
            ) : (
              options.map((option) => (
                <li
                  key={`${option.value}-${option.name}`}
                  onClick={() => handleOptionClick(option.value)}
                  className={listItemClassName}
                >
                  <span className="tw-overflow-hidden tw-block tw-font-bold tw-w-auto">
                    {option.name}
                  </span>
                  <span className="tw-overflow-hidden tw-pl-2 tw-block tw-w-auto tw-text-gray-500">
                    {option.value}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DropdownInputMC;
